import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Star, Send } from 'lucide-react';

export default function Reviews() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ name: '', rating: 5, title: '', body: '', vehicle: '' });
    const [submitting, setSubmitting] = useState(false);

    const load = () => api.get('/reviews/approved').then(({ data }) => setItems(data.items || []));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.body) return;
        setSubmitting(true);
        try {
            await api.post('/reviews', form);
            toast.success('Thanks! Your review is pending approval.');
            setForm({ name: '', rating: 5, title: '', body: '', vehicle: '' });
        } catch {
            toast.error('Could not submit review');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 tracking-widest mb-3">
                    <Star className="w-3.5 h-3.5" /> 5-STAR CLUB
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-white">REVIEWS FROM THE CLUB</h1>
                <p className="mt-3 text-white/60">All reviews are moderated. Favorable, real-customer 5-star reviews appear on the home banner.</p>
            </div>

            <section className="mt-8" data-testid="reviews-approved-grid">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((r) => (
                        <div key={r.id} className="rrm-panel p-5">
                            <div className="flex items-center gap-1 text-amber-400">
                                {Array.from({ length: r.rating }).map((_, i) => <Star key={`${r.id}-star-${i}`} className="w-4 h-4 fill-current" />)}
                            </div>
                            {r.title && <div className="mt-2 font-semibold text-white">{r.title}</div>}
                            <p className="mt-2 text-white/70 text-sm">{r.body}</p>
                            <div className="mt-3 text-xs text-white/50">— {r.name} {r.vehicle ? `· ${r.vehicle}` : ''}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="mt-12 grid lg:grid-cols-2 gap-8">
                <div>
                    <h2 className="font-display text-3xl text-white">LEAVE A REVIEW</h2>
                    <p className="mt-2 text-white/60 text-sm">We post favorable reviews after a quick moderation pass.</p>
                </div>
                <form onSubmit={submit} className="rrm-panel p-5 space-y-3" data-testid="reviews-submit-form">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="review-name-input" />
                        <Field label="Vehicle (optional)" value={form.vehicle} onChange={(v) => setForm({ ...form, vehicle: v })} testid="review-vehicle-input" />
                    </div>
                    <div className="grid sm:grid-cols-[120px_1fr] gap-3 items-end">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Rating</div>
                            <select
                                value={form.rating}
                                onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                                data-testid="review-rating-select"
                                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm"
                            >
                                {[5,4,3,2,1].map((r) => <option key={r} value={r}>{r} ★</option>)}
                            </select>
                        </div>
                        <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="review-title-input" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Review</div>
                        <textarea
                            value={form.body}
                            onChange={(e) => setForm({ ...form, body: e.target.value })}
                            rows={5}
                            required
                            data-testid="review-body-input"
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="reviews-submit-button"
                        className="px-4 py-2.5 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white inline-flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
                    >
                        <Send className="w-4 h-4" /> Submit review
                    </button>
                </form>
            </section>
        </div>
    );
}

function Field({ label, value, onChange, required, testid }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                data-testid={testid}
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
            />
        </div>
    );
}
